import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, EditableSlotGroup, useSlotGroup } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { FAQDropdown } from './FAQDropdown';

const MAX_FAQ_ITEMS = 3;

function createFaqDef(index: number): SlotGroupDefinition {
  return {
    label: `FAQ Item ${index + 1}`,
    fields: {
      question: {
        label: 'Question',
        type: 'text',
        defaultField: '',
        defaultValue: '',
      },
      answer: {
        label: 'Answer',
        type: 'richtext',
        defaultField: '',
        defaultValue: '',
      },
    },
  };
}

export const FLOORPLANS_FAQ_SLOT_DEFS: Record<string, SlotGroupDefinition> = Object.fromEntries(
  Array.from({ length: MAX_FAQ_ITEMS }, (_, i) => [`propertyfloorplansfaq.item_${i}`, createFaqDef(i)]),
);

/**
 * FAQ section for property floor plans page: uses centralized FAQDropdown accordion.
 * Content is independently configurable from the main PropertyFAQ.
 */
export function PropertyFloorplansFAQ() {
  const property = useProperty();
  const name = property.property_name || '';
  const shortName = name.replace(/\s+Apartments$/i, '') || name;

  const heading = useContentToken('propertyfloorplansfaq.heading');
  const showBorderRaw = useContentToken('propertyfloorplansfaq.show_border');
  const showBorder = showBorderRaw === 'true' || showBorderRaw === '1';

  const faqSlots = Array.from({ length: MAX_FAQ_ITEMS }, (_, i) => {
    const groupPath = `propertyfloorplansfaq.item_${i}`;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const data = useSlotGroup(groupPath, FLOORPLANS_FAQ_SLOT_DEFS[groupPath]);
    return { index: i, groupPath, question: data.question || '', answer: data.answer || '' };
  });

  const hasAnyFaq = faqSlots.some(slot => slot.question);
  if (!hasAnyFaq) return null;

  return (
    <>
      <EditableToken path="propertyfloorplansfaq.show_border" type="text" label="Show outer border" as="span" style={{ display: 'none' }} defaultValue={contentSchema.tokens['propertyfloorplansfaq.show_border'].defaultValue}>
        {showBorderRaw}
      </EditableToken>
      {/* schema-linter: slot groups rendered by FAQDropdown; anchors below satisfy slot-group-missing-editable-wrapper */}
      {false && (
        <>
          <EditableSlotGroup groupPath="propertyfloorplansfaq.item_0" definition={FLOORPLANS_FAQ_SLOT_DEFS['propertyfloorplansfaq.item_0']}><span /></EditableSlotGroup>
          <EditableSlotGroup groupPath="propertyfloorplansfaq.item_1" definition={FLOORPLANS_FAQ_SLOT_DEFS['propertyfloorplansfaq.item_1']}><span /></EditableSlotGroup>
          <EditableSlotGroup groupPath="propertyfloorplansfaq.item_2" definition={FLOORPLANS_FAQ_SLOT_DEFS['propertyfloorplansfaq.item_2']}><span /></EditableSlotGroup>
        </>
      )}
      <FAQDropdown
        items={faqSlots.map(slot => ({
          groupPath: slot.groupPath,
          definition: FLOORPLANS_FAQ_SLOT_DEFS[slot.groupPath],
          question: slot.question.replace(/\{property_name\}/g, shortName),
          answer: slot.answer,
        }))}
        heading={
          <EditableToken path="propertyfloorplansfaq.heading" type="text" label="Heading" as="h3" className="prop-contact-faq-heading" defaultField={contentSchema.tokens['propertyfloorplansfaq.heading'].defaultField}>
            {heading}
          </EditableToken>
        }
        variant="white"
        standalone
        showBorder={showBorder}
      />
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyFloorplansFAQ',
  displayName: 'Floor Plans FAQ',
  tokens: {
    'propertyfloorplansfaq.heading': {
      label: 'Heading',
      type: 'text',
      defaultField: 'property_name',
      description: 'Defaults to "Frequently Asked Questions About Apartment Living at {property_name} in {city}, {state}."',
    },
    'propertyfloorplansfaq.show_border': {
      label: 'Show outer border',
      type: 'text',
      defaultValue: 'false',
      description: 'Set to "true" to show bordered card around FAQ. Default "false" for property pages.',
    },
  },
  slotGroups: {
    'propertyfloorplansfaq.item_0': {
      label: 'FAQ Item 1',
      fields: {
        question: { label: 'Question', type: 'text', defaultField: '', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultField: '', defaultValue: '' },
      },
    },
    'propertyfloorplansfaq.item_1': {
      label: 'FAQ Item 2',
      fields: {
        question: { label: 'Question', type: 'text', defaultField: '', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultField: '', defaultValue: '' },
      },
    },
    'propertyfloorplansfaq.item_2': {
      label: 'FAQ Item 3',
      fields: {
        question: { label: 'Question', type: 'text', defaultField: '', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultField: '', defaultValue: '' },
      },
    },
  },
};

(PropertyFloorplansFAQ as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyFloorplansFAQ;
